import React from 'react';
import type { JSONSchema7 } from 'json-schema';
import type { TCAction } from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { StepInputsMappingEditor } from './step-inputs-mapping-editor';
import { InputsFlatList } from './inputs-flat-list';
import { ReferenceOption, StepOutputOption } from './types';

type InputRefs = {
  inputRefs: string[];
  stepOutputRefs: Array<{ stepId: string; key: string }>;
};

type WorkflowInputField = { key: string; required: boolean };

type Props = {
  editable: boolean;
  stepId: string;
  stepInputs: Record<string, unknown>;
  selectedAction?: TCAction;
  actionsLoading: boolean;
  actionsError: unknown;
  workflowInputFields: WorkflowInputField[];
  effectiveInputSchema?: Record<string, unknown>;
  stepOutputOptions: StepOutputOption[];
  referenceOptions: ReferenceOption[];
  inputRefs: InputRefs;
  onChangeInputs: (next: Record<string, unknown>) => void;
};

export const StepInputsTab: React.FC<Props> = ({
  editable,
  stepId,
  stepInputs,
  selectedAction,
  actionsLoading,
  actionsError,
  workflowInputFields,
  effectiveInputSchema,
  stepOutputOptions,
  referenceOptions,
  inputRefs,
  onChangeInputs,
}) => {
  if (!editable) {
    return (
      <div className="space-y-4 text-sm text-slate-600">
        <div className="space-y-2">
          <Text size="small" weight="medium" className="text-slate-700">
            Configuración de entradas
          </Text>
          <InputsFlatList value={stepInputs} />
        </div>
        <div className="space-y-2">
          <Text size="small" weight="medium" className="text-slate-700">
            Referencias
          </Text>
          <div className="space-y-2">
            <div>
              <Text
                size="tiny"
                weight="medium"
                className="text-slate-500 uppercase tracking-wide"
              >
                Entradas del flujo de trabajo
              </Text>
              {inputRefs.inputRefs.length > 0 ? (
                <ul className="list-disc list-inside space-y-1">
                  {inputRefs.inputRefs.map((ref) => (
                    <li key={ref}>{ref}</li>
                  ))}
                </ul>
              ) : (
                <Text size="tiny" className="text-slate-500">
                  No se encontraron referencias a inputs.
                </Text>
              )}
            </div>
            <div>
              <Text
                size="tiny"
                weight="medium"
                className="text-slate-500 uppercase tracking-wide"
              >
                Salidas de pasos anteriores
              </Text>
              {inputRefs.stepOutputRefs.length > 0 ? (
                <ul className="list-disc list-inside space-y-1">
                  {inputRefs.stepOutputRefs.map((ref, index) => (
                    <li key={`${ref.stepId}-${ref.key}-${index}`}>
                      {ref.stepId}.outputs.{ref.key}
                    </li>
                  ))}
                </ul>
              ) : (
                <Text size="tiny" className="text-slate-500">
                  No se encontraron referencias a salidas previas.
                </Text>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 text-sm text-slate-600">
      {actionsLoading ? (
        <Text size="small" className="text-slate-500">
          Cargando catálogo de acciones…
        </Text>
      ) : actionsError ? (
        <div className="border border-rose-200 bg-rose-50 text-rose-700 rounded px-3 py-2 text-sm">
          Error cargando acciones.
        </div>
      ) : !selectedAction ? (
        <EmptyState
          description="No se encontró la acción para este paso."
          appearance="soft"
          variant="neutral"
          size="sm"
          className="py-6"
        />
      ) : (
        <StepInputsMappingEditor
          stepId={stepId}
          action={selectedAction}
          inputs={stepInputs}
          workflowInputKeys={workflowInputFields.map((f) => f.key)}
          workflowInputSchema={effectiveInputSchema as JSONSchema7 | undefined}
          stepOutputs={stepOutputOptions}
          referenceOptions={referenceOptions}
          onChangeInputs={onChangeInputs}
        />
      )}

      <div className="space-y-2">
        <Text size="small" weight="medium" className="text-slate-700">
          Referencias detectadas
        </Text>
        <div className="space-y-2">
          <div>
            <Text
              size="tiny"
              weight="medium"
              className="text-slate-500 uppercase tracking-wide"
            >
              Entradas del flujo de trabajo
            </Text>
            {inputRefs.inputRefs.length > 0 ? (
              <ul className="list-disc list-inside space-y-1">
                {inputRefs.inputRefs.map((ref) => (
                  <li key={ref}>{ref}</li>
                ))}
              </ul>
            ) : (
              <Text size="tiny" className="text-slate-500">
                No se encontraron referencias a inputs.
              </Text>
            )}
          </div>
          <div>
            <Text
              size="tiny"
              weight="medium"
              className="text-slate-500 uppercase tracking-wide"
            >
              Salidas de pasos anteriores
            </Text>
            {inputRefs.stepOutputRefs.length > 0 ? (
              <ul className="list-disc list-inside space-y-1">
                {inputRefs.stepOutputRefs.map((ref, index) => (
                  <li key={`${ref.stepId}-${ref.key}-${index}`}>
                    {ref.stepId}.outputs.{ref.key}
                  </li>
                ))}
              </ul>
            ) : (
              <Text size="tiny" className="text-slate-500">
                No se encontraron referencias a salidas previas.
              </Text>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepInputsTab;
